import { StatusBadge } from '@/components/StatusBadge';
import type { RmmDevice, RmmDeviceBilling, RmmBillingState } from '@/api/rmm';
import { alertPriorityKind, avStatusMeta, daysSince, fmtAge, patchStatusMeta } from '@/lib/rmm-helpers';

/**
 * Device status chips shared by every RMM device list (Devices grid, Sites
 * page). Each renders an em dash when there's nothing to show.
 */

const dash = <span className="text-muted-foreground">—</span>;

/** Online, or "Offline · 3d" — amber once a device hasn't been seen for 30+ days. */
export function DeviceStatusBadge({ d }: { d: RmmDevice }) {
  if (d.online) return <StatusBadge kind="success" dot>Online</StatusBadge>;
  return (
    <span title={d.lastSeen ? `Last seen ${new Date(d.lastSeen).toLocaleString()}` : 'Never seen'}>
      <StatusBadge kind={(daysSince(d.lastSeen) ?? 999) >= 30 ? 'warning' : 'neutral'} dot className="whitespace-nowrap">
        Offline · {fmtAge(d.lastSeen)}
      </StatusBadge>
    </span>
  );
}

export function PatchBadge({ d }: { d: RmmDevice }) {
  const m = patchStatusMeta(d.patchStatus);
  if (!m) return dash;
  const counts = [
    d.patchesApprovedPending ? `${d.patchesApprovedPending} approved pending` : null,
    d.patchesNotApproved ? `${d.patchesNotApproved} not approved` : null,
    d.patchesInstalled ? `${d.patchesInstalled} installed` : null,
  ].filter(Boolean).join(' · ');
  return <span title={counts || undefined}><StatusBadge kind={m.kind} className="whitespace-nowrap">{m.label}</StatusBadge></span>;
}

export function AvBadge({ d }: { d: RmmDevice }) {
  const m = avStatusMeta(d.antivirusStatus);
  if (!m) return dash;
  return <span title={d.antivirusProduct ?? undefined}><StatusBadge kind={m.kind} className="whitespace-nowrap">{m.label}</StatusBadge></span>;
}

export const BILLING_META: Record<RmmBillingState, { label: string; kind: 'success' | 'warning' | 'destructive' | 'neutral' }> = {
  billed:        { label: 'Billed', kind: 'success' },
  'no-service':  { label: 'No service line', kind: 'warning' },
  expired:       { label: 'Contract ended', kind: 'destructive' },
  'no-contract': { label: 'No contract', kind: 'warning' },
  'no-ci':       { label: 'No Autotask CI', kind: 'warning' },
  internal:      { label: 'Internal', kind: 'neutral' },
};

/** Autotask billing for a device: CI → contract → service line. Tooltip names the contract and service. */
export function BillingBadge({ b }: { b: RmmDeviceBilling | undefined }) {
  if (!b) return dash;
  const m = BILLING_META[b.state];
  const title = [
    b.serviceName,
    b.contractName && `${b.contractName}${b.contractEnd ? ` (ends ${b.contractEnd})` : ''}`,
    b.ciId != null ? `Autotask CI #${b.ciId}` : 'No active Autotask configuration item for this device',
  ].filter(Boolean).join('\n');
  return <span title={title}><StatusBadge kind={m.kind} className="whitespace-nowrap">{m.label}</StatusBadge></span>;
}

export function AlertCountBadge({ d }: { d: RmmDevice }) {
  if (d.openAlerts === 0) return dash;
  return (
    <span title={`Highest priority: ${d.topAlertPriority}`}>
      <StatusBadge kind={alertPriorityKind(d.topAlertPriority)}>{d.openAlerts}</StatusBadge>
    </span>
  );
}
